/**
 * Pedestal build: tap/click to stack, pick a plan to auto-build.
 */
import * as THREE from "three";
import { createLegoBrick, BRICK_H } from "../../bricks/LegoBrick";
import { randomPlayColor } from "../../bricks/palette";
import { StudGrid, type SnapPose } from "../../bricks/snap";
import type { GameMode, ModeContext, ModeResult } from "./types";
import { sfx } from "../../audio/sfx";
import { PLANS, planById } from "../../plans/catalog";
import type { Plan, PlanBrick } from "../../plans/types";

export type { ModeResult };

const SIZES = [
  { w: 1, d: 1 },
  { w: 2, d: 1 },
  { w: 2, d: 2 },
  { w: 3, d: 1 },
  { w: 3, d: 2 },
  { w: 4, d: 1 },
] as const;

/** Plans are in studs; shrink so a 6–8 stud model fits the pedestal. */
const PLAN_SCALE = 0.48;
const G = 36;

type Faller = { mesh: THREE.Object3D; ty: number; vy: number };

export class BuildMode implements GameMode {
  readonly duration = 45;
  score = 0;
  bricks = 0;
  timeLeft = 45;

  private root = new THREE.Group();
  private planRoot = new THREE.Group();
  private placed: THREE.Object3D[] = [];
  private grid = new StudGrid();
  private ghost: THREE.Group | null = null;
  private ghostPose: SnapPose | null = null;
  private rot = 0;
  private fallers: Faller[] = [];
  private sizeIdx = 0;
  private color = randomPlayColor();
  private stackHeight = 0;
  private raycaster = new THREE.Raycaster();
  private plane = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0);
  private hit = new THREE.Vector3();
  private camOrbit = 0.35;
  private camElev = 10;
  private camRadius = 13;
  private running = false;
  private elapsed = 0;
  private queue: PlanBrick[] = [];
  private queueT = 0;
  private activePlan: Plan | null = null;
  private unbindHud: (() => void) | null = null;
  private unbindNet: (() => void) | null = null;

  constructor(private ctx: ModeContext) {
    this.root.name = "build-mode";
    this.planRoot.name = "plan-build";
    this.planRoot.scale.setScalar(PLAN_SCALE);
    this.planRoot.position.y = this.ctx.arena.pedestalTop;
    this.plane.constant = -this.ctx.arena.pedestalTop;
  }

  private get scene() {
    return this.ctx.scene;
  }
  private get camera() {
    return this.ctx.camera;
  }
  private get arena() {
    return this.ctx.arena;
  }
  private get input() {
    return this.ctx.input;
  }
  private get hud() {
    return this.ctx.hud;
  }
  private get fx() {
    return this.ctx.fx;
  }

  start() {
    this.reset();
    this.scene.add(this.root);
    this.scene.add(this.planRoot);
    this.running = true;
    this.spawnGhost();
    this.hud.showPlaying();
    this.hud.setPlans(PLANS);
    this.unbindHud = this.hud.bindTouch({
      onPlace: () => this.input.pulse("Space"),
      onSize: () => this.cycleSize(1),
      onColor: () => this.recolor(),
      onPlan: (id) => this.queuePlan(id),
    });
    this.unbindNet = this.ctx.net.on((msg) => this.onNet(msg));
    this.hud.update({
      score: 0,
      timeLeft: this.duration,
      bricks: 0,
      hint: "Ghost snaps to studs · tap Place to drop · T rotate · Q/E size",
    });
  }

  stop() {
    this.running = false;
    this.clearGhost();
    this.unbindHud?.();
    this.unbindHud = null;
    this.unbindNet?.();
    this.unbindNet = null;
    this.scene.remove(this.root);
    this.scene.remove(this.planRoot);
  }

  reset() {
    for (const p of this.placed) {
      p.parent?.remove(p);
    }
    this.placed = [];
    this.grid.clear();
    this.ghostPose = null;
    this.rot = 0;
    this.fallers = [];
    this.score = 0;
    this.bricks = 0;
    this.timeLeft = this.duration;
    this.elapsed = 0;
    this.stackHeight = 0;
    this.sizeIdx = 0;
    this.color = randomPlayColor();
    this.queue = [];
    this.queueT = 0;
    this.activePlan = null;
  }

  update(dt: number): ModeResult | null {
    if (!this.running) return null;

    this.elapsed += dt;
    this.timeLeft = Math.max(0, this.duration - this.elapsed);

    this.handleCamera(dt);
    this.stepFall(dt);
    this.pumpQueue(dt);
    this.handleGhost();
    this.handlePlace();
    this.handleKeys();

    this.hud.update({
      score: this.score,
      timeLeft: this.timeLeft,
      bricks: this.bricks,
    });

    if (this.timeLeft <= 0) {
      this.running = false;
      return {
        score: this.score,
        message: this.endMessage(),
      };
    }
    return null;
  }

  private endMessage(): string {
    if (this.activePlan && this.queue.length === 0 && this.bricks > 0) {
      return `${this.activePlan.name} complete.`;
    }
    if (this.score >= 800) return "Cathedral energy. King material.";
    if (this.score >= 400) return "Solid stack.";
    if (this.bricks >= 5) return "Bricks placed.";
    return "Arena is live.";
  }

  private handleCamera(dt: number) {
    const speed = 1.6;
    if (this.input.isDown("KeyA") || this.input.isDown("ArrowLeft")) this.camOrbit += speed * dt;
    if (this.input.isDown("KeyD") || this.input.isDown("ArrowRight")) this.camOrbit -= speed * dt;
    if (this.input.isDown("KeyW") || this.input.isDown("ArrowUp")) this.camElev = Math.min(16, this.camElev + 6 * dt);
    if (this.input.isDown("KeyS") || this.input.isDown("ArrowDown")) this.camElev = Math.max(4, this.camElev - 6 * dt);

    this.camOrbit -= this.input.orbit.dx * 0.007;
    this.camElev = Math.min(16, Math.max(4, this.camElev + this.input.orbit.dy * 0.02));
    this.camRadius = Math.min(22, Math.max(7, this.camRadius - this.input.pinch * 0.03));

    const targetY = this.arena.pedestalTop + this.stackHeight * 0.35;
    this.camera.position.set(
      Math.sin(this.camOrbit) * this.camRadius,
      this.camElev,
      Math.cos(this.camOrbit) * this.camRadius,
    );
    this.camera.lookAt(0, targetY, 0);
  }

  private handleGhost() {
    if (!this.ghost) return;
    this.raycaster.setFromCamera(
      new THREE.Vector2(this.input.pointer.x, this.input.pointer.y),
      this.camera,
    );
    const ok = this.raycaster.ray.intersectPlane(this.plane, this.hit);
    if (!ok) return;

    const { w, d } = this.dims();
    const pose = this.grid.snap(w, d, this.hit.x, this.hit.z, this.arena.pedestalTop);
    this.ghostPose = pose;
    if (!pose) {
      this.ghost.position.set(this.hit.x, this.arena.pedestalTop + BRICK_H / 2, this.hit.z);
      this.tintGhost(false);
      return;
    }
    this.ghost.position.set(pose.x, pose.y, pose.z);
    this.tintGhost(true);
  }

  private handlePlace() {
    const place = this.input.tapPlace || this.input.justPressed("Space");
    if (!place || !this.ghostPose || this.queue.length) return;
    this.lock(this.ghostPose, this.color, false);
    this.color = randomPlayColor();
    this.sizeIdx = Math.floor(Math.random() * SIZES.length);
    this.spawnGhost();
  }

  private handleKeys() {
    if (this.input.justPressed("KeyQ")) this.cycleSize(-1);
    if (this.input.justPressed("KeyE")) this.cycleSize(1);
    if (this.input.justPressed("KeyR")) this.recolor();
    if (this.input.justPressed("KeyT")) {
      this.rot = 1 - this.rot;
      this.spawnGhost();
      sfx.ui();
    }
  }

  private cycleSize(dir: number) {
    this.sizeIdx = (this.sizeIdx + dir + SIZES.length) % SIZES.length;
    this.spawnGhost();
    sfx.ui();
  }

  private recolor() {
    this.color = randomPlayColor();
    this.spawnGhost();
    sfx.ui();
  }

  private dims() {
    const s = SIZES[this.sizeIdx]!;
    return this.rot ? { w: s.d, d: s.w } : { w: s.w, d: s.d };
  }

  private onNet(msg: { t: string; [k: string]: unknown }) {
    if (msg.t === "brick") {
      const pose = this.grid.worldOf(
        Number(msg.sx),
        Number(msg.sz),
        Number(msg.w),
        Number(msg.d),
        Number(msg.layer),
        this.arena.pedestalTop,
      );
      this.lock(pose, Number(msg.color), true);
    } else if (msg.t === "plan" && typeof msg.id === "string") {
      this.queuePlan(msg.id, true);
    }
  }

  private queuePlan(id: string, remote = false) {
    const plan = planById(id);
    if (!plan) return;
    this.activePlan = plan;
    this.queue = [...plan.bricks];
    this.queueT = 0;
    this.hud.setActivePlan(plan.name, plan.id);
    if (!remote && this.ctx.net.connected) this.ctx.net.send({ t: "plan", id });
    this.hud.update({
      score: this.score,
      timeLeft: this.timeLeft,
      bricks: this.bricks,
      hint: `Auto-building ${plan.name}…`,
    });
    sfx.start();
  }

  private pumpQueue(dt: number) {
    if (!this.queue.length) return;
    this.queueT += dt;
    const step = 0.08;
    while (this.queueT >= step && this.queue.length) {
      this.queueT -= step;
      const b = this.queue.shift()!;
      this.stampPlan(b);
    }
    if (!this.queue.length && this.activePlan) {
      this.hud.update({
        score: this.score,
        timeLeft: this.timeLeft,
        bricks: this.bricks,
        hint: `${this.activePlan.name} locked. Keep stacking or pick another.`,
      });
    }
  }

  private lock(pose: SnapPose, color: number, remote: boolean) {
    this.grid.occupy(pose);
    const brick = createLegoBrick({ w: pose.w, d: pose.d }, color, { emissive: Math.random() > 0.7 });
    brick.position.set(pose.x, pose.y + 1.6, pose.z);
    this.root.add(brick);
    this.placed.push(brick);
    this.fallers.push({ mesh: brick, ty: pose.y, vy: 0 });
    this.afterStamp(brick, pose.w, pose.d, pose.y, color);
    if (!remote && this.ctx.net.connected) {
      this.ctx.net.send({
        t: "brick",
        sx: pose.sx,
        sz: pose.sz,
        w: pose.w,
        d: pose.d,
        layer: pose.layer,
        color,
      });
    }
  }

  private stepFall(dt: number) {
    for (let i = this.fallers.length - 1; i >= 0; i--) {
      const f = this.fallers[i]!;
      f.vy += G * dt;
      f.mesh.position.y -= f.vy * dt;
      if (f.mesh.position.y <= f.ty) {
        f.mesh.position.y = f.ty;
        this.fallers.splice(i, 1);
        sfx.place();
        this.fx.shake(0.1, 0.08);
      }
    }
  }

  private stampPlan(b: PlanBrick) {
    const h = b.h ?? BRICK_H;
    const brick = createLegoBrick({ w: b.w, d: b.d, h }, b.color, { emissive: Math.random() > 0.55 });
    brick.position.set(b.x, b.layer * BRICK_H + h / 2, b.z);
    this.planRoot.add(brick);
    this.placed.push(brick);
    const y = this.arena.pedestalTop + (b.layer * BRICK_H + h / 2) * PLAN_SCALE;
    this.afterStamp(brick, b.w, b.d, y, b.color);
  }

  private afterStamp(brick: THREE.Object3D, w: number, d: number, worldY: number, color: number) {
    this.bricks += 1;
    this.stackHeight = Math.max(this.stackHeight, worldY - this.arena.pedestalTop);
    this.score += w * d * 25 + Math.floor(this.stackHeight * 10);
    this.fx.burst(brick.position, color, 6);
  }

  private spawnGhost() {
    this.clearGhost();
    const { w, d } = this.dims();
    this.ghost = createLegoBrick({ w, d }, this.color, { emissive: true, castShadow: false });
    this.ghost.traverse((o) => {
      if (o instanceof THREE.Mesh && o.material instanceof THREE.MeshStandardMaterial) {
        o.material = o.material.clone();
        o.material.transparent = true;
        o.material.opacity = 0.55;
        o.material.depthWrite = false;
      }
    });
    this.root.add(this.ghost);
    this.tintGhost(true);
  }

  private tintGhost(ok: boolean) {
    if (!this.ghost) return;
    this.ghost.traverse((o) => {
      if (o instanceof THREE.Mesh && o.material instanceof THREE.MeshStandardMaterial) {
        o.material.opacity = ok ? 0.55 : 0.28;
        o.material.emissiveIntensity = ok ? 0.35 : 0.05;
      }
    });
  }

  private clearGhost() {
    if (this.ghost) {
      this.root.remove(this.ghost);
      this.ghost = null;
    }
  }
}
